/**
 * ฟอร์มสมัครสมาชิก ส่วนที่ 1 (เพศ + อายุ) และส่วนที่ 2 แบบสำรวจตอนสมัคร (ไม่บังคับ)
 * ค่าตั้งคำถาม/ตัวเลือกมาจากแอดมิน — memberApi คืน surveyConfig (CF member/memberSurvey.js · member_program_settings/joinSurvey)
 * CF เก่าไม่ส่ง surveyConfig = ไม่แสดงส่วนที่ 2 (ฟอร์มเดิม)
 */

export const GENDERS = ['หญิง', 'ชาย', 'ไม่ระบุ'] as const

export type SurveyQuestionId = 'history' | 'deity' | 'news' | 'source'
export type SurveyQuestion = { id: SurveyQuestionId; label: string; multi: boolean; counted: boolean; sensitive: boolean; enabled: boolean; options: string[] }
export type SurveyConfig = { enabled: boolean; bonusPoints: number; questions: SurveyQuestion[] }
export type SurveyAnswers = Partial<Record<SurveyQuestionId, string[]>>
export type MemberSurvey = { answers: SurveyAnswers; beliefConsent: boolean; answeredAt: number; bonusAwarded: boolean; bonusAwardedPoints: number }

const THAI_MONTHS = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม']

/** อายุ "อายุ X ปี Y เดือน" จากวัน / เดือน (ชื่อไทยหรือเลข) / ปี พ.ศ. · ไม่ครบ/วันไม่มีจริง/อนาคต = '' — ตรรกะเดียวกับ Admin ageText */
export function ageText(day: string, month: string, yearBE: string, now: Date = new Date()): string {
  const d = Number(day)
  const mNum = Number(month)
  const m = Number.isInteger(mNum) && mNum >= 1 ? mNum : THAI_MONTHS.indexOf(month) + 1
  const raw = Number(yearBE)
  const y = raw > 2400 ? raw - 543 : raw
  if (![d, m, y].every((n) => Number.isInteger(n) && n > 0) || m > 12 || y < 1900) return ''
  const birth = new Date(y, m - 1, d)
  if (birth.getFullYear() !== y || birth.getMonth() !== m - 1 || birth.getDate() !== d || birth > now) return ''
  const months = (now.getFullYear() - y) * 12 + (now.getMonth() - (m - 1)) - (now.getDate() < d ? 1 : 0)
  return `อายุ ${Math.floor(months / 12)} ปี ${months % 12} เดือน`
}

/** คำถามที่แสดง — ข้อ history ไม่ถามคนที่มีออร์เดอร์แล้ว (ระบบรู้จากประวัติ) */
export function visibleQuestions(config: SurveyConfig | null | undefined, hasOrders: boolean): SurveyQuestion[] {
  if (!config || !config.enabled) return []
  return config.questions.filter((q) => q.enabled && q.options.length > 0 && !(q.id === 'history' && hasOrders))
}

/** ความคืบหน้าแต้ม — นับเฉพาะข้อ counted · ข้อ sensitive ไม่ยินยอม = นับว่าตอบแล้ว (เหมือน CF isSurveyComplete) */
export function surveyProgress(config: SurveyConfig | null | undefined, answers: SurveyAnswers, beliefConsent: boolean): { done: number; total: number } {
  const counted = config && config.enabled ? config.questions.filter((q) => q.enabled && q.options.length > 0 && q.counted) : []
  const done = counted.filter((q) => (q.sensitive && !beliefConsent) || (answers[q.id] || []).length > 0).length
  return { done, total: counted.length }
}

/** แตะตัวเลือก — ข้อเลือกเดียวแทนที่ · แตะซ้ำ = เอาออก */
export function toggleAnswer(answers: SurveyAnswers, q: Pick<SurveyQuestion, 'id' | 'multi'>, option: string): SurveyAnswers {
  const cur = answers[q.id] || []
  const has = cur.includes(option)
  const next = q.multi ? (has ? cur.filter((x) => x !== option) : [...cur, option]) : (has ? [] : [option])
  return { ...answers, [q.id]: next }
}

/** ส่งให้ CF — ไม่ยินยอม = ไม่ส่งคำตอบข้อ sensitive เลย (CF กรองซ้ำ) */
export function buildSurveyPayload(config: SurveyConfig | null | undefined, answers: SurveyAnswers, beliefConsent: boolean): { answers: SurveyAnswers; beliefConsent: boolean } | null {
  if (!config || !config.enabled) return null
  const out: SurveyAnswers = {}
  config.questions.forEach((q) => {
    if (!q.enabled || (q.sensitive && !beliefConsent)) return
    const list = (answers[q.id] || []).filter((x) => q.options.includes(x))
    if (list.length) out[q.id] = list
  })
  return { answers: out, beliefConsent }
}
